import { goalsApi } from '../../services/goalsApi';
import { withWalletTypeData, withWalletTypeParams } from '../../utils/walletType';

const state = {
  goals: [],
  goalTypes: [],
  badges: [],
  currentGoal: null,
  loading: false,
  error: null,
};

/* eslint-disable no-param-reassign */
const mutations = {
  SET_LOADING(currentState, loading) {
    currentState.loading = loading;
  },

  SET_ERROR(currentState, error) {
    currentState.error = error;
  },

  CLEAR_ERROR(currentState) {
    currentState.error = null;
  },

  SET_GOALS(currentState, goals) {
    currentState.goals = goals;
  },

  SET_GOAL_TYPES(currentState, goalTypes) {
    currentState.goalTypes = goalTypes;
  },

  SET_BADGES(currentState, badges) {
    currentState.badges = badges;
  },

  SET_CURRENT_GOAL(currentState, goal) {
    currentState.currentGoal = goal;
  },

  ADD_GOAL(currentState, goal) {
    currentState.goals.unshift(goal);
  },

  UPDATE_GOAL(currentState, updatedGoal) {
    const index = currentState.goals.findIndex((goal) => goal.id === updatedGoal.id);
    if (index !== -1) {
      currentState.goals.splice(index, 1, updatedGoal);
    }
    if (currentState.currentGoal && currentState.currentGoal.id === updatedGoal.id) {
      currentState.currentGoal = updatedGoal;
    }
  },

  REMOVE_GOAL(currentState, goalId) {
    currentState.goals = currentState.goals.filter((goal) => goal.id !== goalId);
    if (currentState.currentGoal && currentState.currentGoal.id === goalId) {
      currentState.currentGoal = null;
    }
  },

  UPDATE_GOAL_PROGRESS(currentState, { goalId, currentAmount, progressPercentage }) {
    const goal = currentState.goals.find((g) => g.id === goalId);
    if (goal) {
      goal.current_amount = currentAmount;
      goal.progress_percentage = progressPercentage;
    }
    // Also update currentGoal if it's the same one
    if (currentState.currentGoal && currentState.currentGoal.id === goalId) {
      currentState.currentGoal.current_amount = currentAmount;
      currentState.currentGoal.progress_percentage = progressPercentage;
    }
  },
};
/* eslint-enable no-param-reassign */

const actions = {
  async fetchGoals({ commit, rootGetters }, filters = {}) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const params = withWalletTypeParams({ getters: rootGetters }, filters);
      const response = await goalsApi.getUserGoals(params);
      commit('SET_GOALS', response.data.goals || []);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch goals';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchGoalTypes({ commit }) {
    try {
      const response = await goalsApi.getGoalTypes();
      commit('SET_GOAL_TYPES', response.data.goalTypes || []);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch goal types';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  async fetchGoalDetails({ commit, rootGetters }, goalId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const params = withWalletTypeParams({ getters: rootGetters });
      const response = await goalsApi.getGoalDetails(goalId, params);
      commit('SET_CURRENT_GOAL', response.data.goal);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch goal details';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async createGoal({ commit, rootGetters }, goalData) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const payload = withWalletTypeData({ getters: rootGetters }, goalData);
      const response = await goalsApi.createGoal(payload);
      commit('ADD_GOAL', response.data.goal);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create goal';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateGoal({ commit }, { goalId, goalData }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const response = await goalsApi.updateGoal(goalId, goalData);
      commit('UPDATE_GOAL', response.data.goal);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update goal';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async deleteGoal({ commit }, goalId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      await goalsApi.deleteGoal(goalId);
      commit('REMOVE_GOAL', goalId);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete goal';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async contributeToGoal({ commit, rootGetters }, { goalId, contribution }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const payload = withWalletTypeData({ getters: rootGetters }, contribution);
      const response = await goalsApi.contributeToGoal(goalId, payload);
      const { newBalance, progressPercentage } = response.data;
      commit('UPDATE_GOAL_PROGRESS', {
        goalId,
        currentAmount: newBalance,
        progressPercentage,
      });
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to make contribution';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchUserBadges({ commit, rootGetters }) {
    try {
      const params = withWalletTypeParams({ getters: rootGetters });
      const response = await goalsApi.getUserBadges(params);
      commit('SET_BADGES', response.data.badges || []);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch badges';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR');
  },

  clearCurrentGoal({ commit }) {
    commit('SET_CURRENT_GOAL', null);
  },
};

const getters = {
  activeGoals: (currentState) => (currentState.goals || []).filter((goal) => goal.status === 'active'),
  completedGoals: (currentState) => (currentState.goals || []).filter((goal) => goal.status === 'completed'),
  goalsByCategory: (currentState) => {
    const categories = {};
    (currentState.goals || []).forEach((goal) => {
      const category = (goal.goal_type && goal.goal_type.category) || 'other';
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(goal);
    });
    return categories;
  },
  totalGoalValue: (currentState) => (currentState.goals || [])
    .reduce((total, goal) => total + (goal.target_amount || 0), 0),
  // eslint-disable-next-line camelcase
  totalSavedAmount: (currentState) => (currentState.goals || [])
    .reduce((total, goal) => total + (goal.current_amount || 0), 0),
  overallProgress: (currentState, currentGetters) => {
    if (currentGetters.totalGoalValue === 0) return 0;
    return Math.round((currentGetters.totalSavedAmount / currentGetters.totalGoalValue) * 100);
  },
  goalTypes: (currentState) => currentState.goalTypes,
  badges: (currentState) => currentState.badges,
  isLoading: (currentState) => currentState.loading,
  error: (currentState) => currentState.error,
  currentGoal: (currentState) => currentState.currentGoal,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
